import type { PostgrestError } from '@supabase/supabase-js';

/**
 * Check if an error is an authentication/authorization error
 * These indicate the user's session has expired or is invalid
 */
export function isAuthError(error: PostgrestError | null | undefined): boolean {
  if (!error) return false;

  // 403 = Forbidden (RLS policy violation - auth.uid() is null)
  // 401 = Unauthorized (Invalid/expired JWT)
  // PGRST301 = JWT expired
  // 42501 = Insufficient privilege (RLS violation)
  const authCodes = ['PGRST301', '42501'];
  const authStatuses = [401, 403];

  // Check HTTP status code (from error.code parsed as number, or check message)
  const statusCode = parseInt(error.code || '', 10);
  if (!isNaN(statusCode) && authStatuses.includes(statusCode)) {
    return true;
  }

  // Check PostgreSQL error codes
  if (authCodes.includes(error.code || '')) {
    return true;
  }

  // Check error message for common auth failure patterns
  const authMessages = [
    'JWT expired',
    'Invalid JWT',
    'row-level security',
    'permission denied',
    'not authenticated',
  ];

  const errorMessage = (error.message || '').toLowerCase();
  return authMessages.some((msg) => errorMessage.includes(msg.toLowerCase()));
}

/**
 * Handle auth errors by redirecting to login
 * Call this when you detect an auth error in an API response
 */
export function handleAuthError(): void {
  // Only redirect if we're on a protected route
  if (window.location.pathname.startsWith('/app')) {
    console.warn('Auth error detected, redirecting to login');
    // Use window.location to force a full page refresh and clear state
    window.location.href = '/login?reason=session_expired';
  }
}

/**
 * Wrapper function to check for auth errors in API responses
 * Use this in catch blocks or after checking error responses
 */
export function checkAndHandleAuthError(
  error: PostgrestError | null | undefined
): boolean {
  if (isAuthError(error)) {
    handleAuthError();
    return true;
  }
  return false;
}
